import {
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  AfterViewInit,
} from '@angular/core';
import {
  NavigationStart,
  Router,
  RouterModule,
  RouterOutlet,
} from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Subscription } from 'rxjs';
import { AuthService } from './api/services/auth.service';

@Component({
  selector: 'app-root',
  imports: [
    CommonModule,
    RouterOutlet,
    RouterModule,
    ReactiveFormsModule,
    FormsModule,
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  routerSubscription: Subscription | undefined;
  lastVisitedUrl: string | null = null;
  isReconnecting = false;

  constructor(
    private router: Router,
    private authService: AuthService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {}

  private messageHandler = this.onMessage.bind(this);

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // Intercept browser back/forward navigation.
    // When Angular detects a popstate (back/forward button), NavigationStart fires
    // with navigationTrigger === 'popstate'. We redirect away from /login and
    // /workflow/... routes to the executive dashboard when a token is present.
    this.routerSubscription = this.router.events.subscribe((event) => {
      if (
        event instanceof NavigationStart &&
        event.navigationTrigger === 'popstate'
      ) {
        const targetUrl = event.url;
        const shouldRedirect =
          targetUrl === '/login' || targetUrl.startsWith('/workflow/');

        if (shouldRedirect) {
          const token = localStorage.getItem('accessToken');
          const dest = token ? '/eciso/executive-dashboard' : '/login';
          this.router.navigate([dest], { replaceUrl: true });
        }
      }
    });

    // Parse access token from iframe URL on load (if parent embeds token in src)
    try {
      const token = this.parseTokenFromUrl(window.location.href);
      if (token) {
        localStorage.setItem('accessToken', token);
      }
    } catch (e) {}

    // If navigating/redirecting to executive-dashboard with no localStorage stored, store all keys
    try {
      if (window.location.href.includes('/eciso/executive-dashboard')) {
        if (localStorage.length === 0 || !localStorage.getItem('accessToken')) {
          const url = new URL(window.location.href);
          let params = url.searchParams;

          if (url.hash && url.hash.includes('?')) {
            const hashQuery = url.hash.split('?')[1];
            params = new URLSearchParams(hashQuery);
          } else if (
            url.hash &&
            !url.hash.includes('/') &&
            url.hash.includes('=')
          ) {
            const hashPath = url.hash.startsWith('#')
              ? url.hash.substring(1)
              : url.hash;
            params = new URLSearchParams(hashPath);
          }

          params.forEach((value, key) => {
            localStorage.setItem(key, value);
          });
        }
      }
    } catch (e) {}

    // SSO popup window detection and message dispatcher
    try {
      if (window.opener) {
        const url = new URL(window.location.href);
        let params = url.searchParams;

        if (url.hash && url.hash.includes('?')) {
          const hashQuery = url.hash.split('?')[1];
          params = new URLSearchParams(hashQuery);
        } else if (
          url.hash &&
          !url.hash.includes('/') &&
          url.hash.includes('=')
        ) {
          const hashPath = url.hash.startsWith('#')
            ? url.hash.substring(1)
            : url.hash;
          params = new URLSearchParams(hashPath);
        }

        const token =
          params.get('accessToken') ||
          params.get('token') ||
          localStorage.getItem('accessToken');
        if (token) {
          window.opener.postMessage(
            {
              accessToken: token,
              email: params.get('email') || localStorage.getItem('email'),
              pmSetup: params.get('pmSetup') || localStorage.getItem('pmSetup'),
              refreshToken:
                params.get('refreshToken') ||
                localStorage.getItem('refreshToken'),
              role: params.get('role') || localStorage.getItem('role'),
            },
            '*',
          );
          try {
            window.opener.focus();
          } catch (_) {}
          window.close();
        }
      }
    } catch (e) {}

    // Iframe token dispatcher
    try {
      if (window.parent && window.parent !== window) {
        const token = localStorage.getItem('accessToken');
        if (token) {
          window.parent.postMessage(
            {
              type: 'sso-tokens',
              accessToken: token,
              email: localStorage.getItem('email'),
              pmSetup: localStorage.getItem('pmSetup'),
              refreshToken: localStorage.getItem('refreshToken'),
              role: localStorage.getItem('role'),
            },
            '*',
          );
        }
      }
    } catch (e) {}

    // Listen for postMessage from parent (eciso-skin) to receive tokens
    window.addEventListener('message', this.messageHandler, false);
  }

  ngAfterViewInit() {}

  @HostListener('window:resize')
  onResize(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const sidebar = document.getElementById('sidebar');
    if (!sidebar) return;

    const KTDrawer = (window as any)?.KTDrawer;
    if (!KTDrawer) return;

    const drawer = KTDrawer.getInstance(sidebar);
    if (!drawer) return;

    drawer?.update();
    sidebar.classList.remove('hidden');
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
  }

  getProfileDetails(token: any) {
    return this.authService.getProfile(token);
  }

  private onMessage(event: MessageEvent) {
    if (!event || !event.data) return;
    // In production, validate event.origin here to allowed parent origin
    const data = event.data;
    if (data.accessToken && typeof data.accessToken === 'string') {
      localStorage.setItem('accessToken', data.accessToken);
      if (data.email) {
        localStorage.setItem('email', data.email);
      }
      if (data.pmSetup !== undefined && data.pmSetup !== null) {
        localStorage.setItem('pmSetup', String(data.pmSetup));
      }
      if (data.refreshToken) {
        localStorage.setItem('refreshToken', data.refreshToken);
      }
      if (data.role) {
        localStorage.setItem(
          'role',
          typeof data.role === 'string' ? data.role : JSON.stringify(data.role),
        );
      }
    }
  }

  private parseTokenFromUrl(href: string): string | null {
    try {
      const url = new URL(href, window.location.origin);
      const q =
        url.searchParams.get('accessToken') || url.searchParams.get('token');
      if (q) return q;
      if (url.hash) {
        const hash = url.hash.startsWith('#')
          ? url.hash.substring(1)
          : url.hash;
        const params = new URLSearchParams(hash);
        return params.get('accessToken') || params.get('token');
      }
    } catch (e) {
      return null;
    }
    return null;
  }
}
